import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchStockData } from '../lib/scoring';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { formatCurrency, formatPercent } from '../lib/utils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

export function StockDetail() {
  const { symbol } = useParams();
  const { isPremium } = useAuth();
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isPremium) return;
    setLoading(true);
    setError(null);
    fetchStockData(symbol)
      .then((data) => setStock(data))
      .catch((err) => setError(err.message || 'Error al cargar los datos.'))
      .finally(() => setLoading(false));
  }, [symbol, isPremium]);

  if (!isPremium) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <div className="w-24 h-24 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 text-brand-red">
          <SafeIcon icon={FiIcons.FiLock} className="text-4xl" />
        </div>
        <h2 className="text-3xl font-bold mb-4">Contenido Exclusivo Premium</h2>
        <p className="text-gray-600 mb-8 text-lg">
          El análisis profundo, valor justo exacto y proyecciones a 10 años están reservados para
          nuestros miembros Premium.
        </p>
        <Link to="/settings">
          <Button className="text-lg px-8 py-3">Actualizar Cuenta Ahora</Button>
        </Link>
        <div className="mt-4">
          <Link to="/dashboard" className="text-brand-slate hover:text-brand-dark">
            Volver al Buscador
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-4 w-24 bg-gray-200 rounded" />
        <div className="flex justify-between">
          <div className="space-y-2">
            <div className="h-10 w-32 bg-gray-200 rounded" />
            <div className="h-4 w-48 bg-gray-100 rounded" />
          </div>
          <div className="space-y-2 text-right">
            <div className="h-10 w-28 bg-gray-200 rounded" />
            <div className="h-5 w-20 bg-gray-100 rounded-full ml-auto" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-6 bg-gray-200 rounded-xl p-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 w-20 bg-gray-300 rounded" />
              <div className="h-7 w-24 bg-gray-300 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto text-center py-16">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 text-brand-red">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="text-3xl" />
        </div>
        <h2 className="text-2xl font-bold mb-2 text-brand-dark">Error al Cargar</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <Link to="/dashboard">
          <Button variant="outline">Volver al Buscador</Button>
        </Link>
      </div>
    );
  }

  if (!stock) return null;

  if (stock.noData) {
    return (
      <div className="space-y-8">
        <Link
          to="/dashboard"
          className="text-brand-slate hover:text-brand-dark flex items-center gap-2 w-fit"
        >
          <SafeIcon icon={FiIcons.FiArrowLeft} /> Volver
        </Link>
        <Card className="p-8 max-w-xl">
          <div className="flex items-start gap-4">
            <SafeIcon icon={FiIcons.FiAlertCircle} className="text-amber-500 text-2xl flex-shrink-0 mt-0.5" />
            <div>
              <h1 className="text-2xl font-bold text-brand-dark mb-2">{stock.symbol}</h1>
              <p className="text-gray-700 mb-4">{stock.message}</p>
              <Badge status="Sin datos" />
            </div>
          </div>
          <Link to="/dashboard" className="inline-block mt-6">
            <Button variant="outline">Volver al Buscador</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const projectionData = Array.from({ length: 11 }, (_, i) => ({
    year: new Date().getFullYear() + i,
    'Precio Actual': parseFloat((stock.price * Math.pow(1.07, i)).toFixed(2)),
    'Valor Justo': parseFloat((stock.fairValue * Math.pow(1.07, i)).toFixed(2)),
  }));

  const scoreBreakdown = [
    {
      label: 'Historial ROIC',
      value: stock.score >= 70 ? 'Fuerte' : stock.score >= 40 ? 'Estable' : 'Débil',
      color:
        stock.score >= 70 ? 'text-green-600' : stock.score >= 40 ? 'text-yellow-600' : 'text-red-600',
    },
    {
      label: 'Crecimiento Capital',
      value: stock.score >= 60 ? 'Fuerte' : 'Moderado',
      color: stock.score >= 60 ? 'text-green-600' : 'text-yellow-600',
    },
    {
      label: 'Crecimiento EPS',
      value: stock.eps_ttm > stock.eps_4y_ago ? 'Fuerte' : 'Débil',
      color: stock.eps_ttm > stock.eps_4y_ago ? 'text-green-600' : 'text-red-600',
    },
    {
      label: 'Crecimiento Ventas',
      value: stock.score >= 50 ? 'Positivo' : 'Negativo',
      color: stock.score >= 50 ? 'text-green-600' : 'text-red-600',
    },
  ];

  return (
    <div className="space-y-8">
      <Link
        to="/dashboard"
        className="text-brand-slate hover:text-brand-dark flex items-center gap-2 w-fit"
      >
        <SafeIcon icon={FiIcons.FiArrowLeft} /> Volver
      </Link>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-4xl font-bold text-brand-dark">{stock.symbol}</h1>
          <p className="text-xl text-gray-500">{stock.name}</p>
        </div>
        <div className="text-right">
          <p className="text-4xl font-bold text-brand-dark">{formatCurrency(stock.price)}</p>
          <div className="mt-2 flex justify-end">
            <Badge status={stock.status} className="text-sm px-3 py-1" />
          </div>
        </div>
      </div>

      <Card className="p-6 col-span-3 grid grid-cols-2 md:grid-cols-4 gap-6 bg-brand-dark text-white">
        <div>
          <p className="text-gray-400 text-sm mb-1">Valor Justo Calculado</p>
          <p className="text-2xl font-bold text-brand-red">{formatCurrency(stock.fairValue)}</p>
        </div>
        <div>
          <p className="text-gray-400 text-sm mb-1">Margen de Seguridad</p>
          <p
            className={`text-2xl font-bold ${
              stock.mos >= 0.12
                ? 'text-green-400'
                : stock.mos <= -0.1
                  ? 'text-red-400'
                  : 'text-yellow-300'
            }`}
          >
            {formatPercent(stock.mos)}
          </p>
        </div>
        <div>
          <p className="text-gray-400 text-sm mb-1">Puntaje de Calidad</p>
          <p className="text-2xl font-bold">{stock.score} / 100</p>
        </div>
        <div>
          <p className="text-gray-400 text-sm mb-1">EPS TTM</p>
          <p className="text-2xl font-bold">{formatCurrency(stock.eps_ttm)}</p>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-bold mb-4 border-b pb-2">Desglose del Puntaje</h3>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-500">Puntaje Global</span>
              <span className="font-bold">{stock.score}/100</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className="bg-brand-red h-2 rounded-full transition-all"
                style={{ width: `${stock.score}%` }}
              />
            </div>
          </div>
          <ul className="space-y-3">
            {scoreBreakdown.map((item) => (
              <li key={item.label} className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">{item.label}</span>
                <span className={`font-bold text-sm ${item.color}`}>{item.value}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">EPS hace 4 años</span>
              <span className="font-medium">{formatCurrency(stock.eps_4y_ago)}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-gray-500">EPS actual (TTM)</span>
              <span className="font-medium">{formatCurrency(stock.eps_ttm)}</span>
            </div>
          </div>
        </Card>

        <Card className="p-6 col-span-1 md:col-span-2">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiTrendingUp} />
            Proyección a 10 Años
          </h3>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={projectionData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `$${v}`}
                  tick={{ fontSize: 11 }}
                />
                <RechartsTooltip
                  formatter={(value, name) => [formatCurrency(value), name]}
                  labelFormatter={(label) => `Año ${label}`}
                  contentStyle={{
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="Precio Actual"
                  stroke="#64748B"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="Valor Justo"
                  stroke="#C0392B"
                  strokeWidth={3}
                  dot={{ r: 3, fill: '#C0392B' }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-6 mt-3 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-0.5 bg-brand-red inline-block rounded" />
              Valor Justo
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-0.5 bg-brand-slate inline-block rounded" />
              Precio Actual
            </span>
          </div>
        </Card>
      </div>
    </div>
  );
}
